import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { resolveImageUrl } from '../lib/imageUrl';
import { formatPrice } from '../lib/formatPrice';
import { prixLigne } from '../contexts/CartContext';
import {
  FRAIS_LIEGE,
  FRAIS_PERIPHERIE,
  FRAIS_HORS_LIEGE,
  MINIMUM_HORS_LIEGE,
  delaiLivraison,
} from '../lib/delivery';
import { useNavigate } from 'react-router-dom';

export default function CartDrawer() {
  const { t, getLocalizedField, language } = useLanguage();
  const { items, isOpen, closeCart, updateQuantity, removeItem, subtotal, itemCount } = useCart();
  const navigate = useNavigate();

  // Montant restant avant de pouvoir être livré hors région liégeoise.
  const manqueHorsLiege = Math.max(0, MINIMUM_HORS_LIEGE - subtotal);

  const handleCheckout = () => {
    closeCart();
    navigate('/checkout');
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            data-testid="cart-backdrop"
          />
          
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-bold font-heading text-text_primary">{t('yourCart')}</h2>
                <span className="px-2 py-0.5 bg-primary/10 text-primary text-sm font-medium rounded-full">
                  {itemCount}
                </span>
              </div>
              <button
                onClick={closeCart}
                className="p-2 rounded-full hover:bg-secondary/50 transition-colors"
                data-testid="close-cart-button"
                aria-label="Close cart"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Items */}
            <div className="flex-1 overflow-y-auto p-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <ShoppingBag className="w-16 h-16 text-text_secondary/30 mb-4" />
                  <p className="text-text_secondary mb-4">{t('emptyCart')}</p>
                  <button
                    onClick={closeCart}
                    className="px-6 py-2 bg-secondary text-text_primary rounded-full font-medium hover:bg-secondary_hover transition-colors"
                    data-testid="continue-shopping-button"
                  >
                    {t('continueShopping')}
                  </button>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map(item => (
                    <motion.li
                      key={item.cle}
                      layout
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex gap-4 p-3 bg-secondary/30 rounded-xl"
                      data-testid={`cart-item-${item.cle}`}
                    >
                      {/* Image */}
                      <img
                        src={resolveImageUrl(item.product.image_url)}
                        alt={getLocalizedField(item.product, 'name')}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      
                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-text_primary text-sm line-clamp-1">
                          {getLocalizedField(item.product, 'name')}
                        </h3>
                        {/* Le poids retenu : sans lui, deux lignes du même
                            produit seraient indiscernables. */}
                        {item.declinaison?.format && (
                          <p className="text-xs text-text_secondary mt-0.5">{item.declinaison.format}</p>
                        )}
                        <p className="text-primary font-semibold mt-1">
                          {formatPrice(prixLigne(item), language)}
                        </p>
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateQuantity(item.cle, item.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-border hover:bg-secondary transition-colors"
                            data-testid={`decrease-quantity-${item.cle}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-medium" data-testid={`quantity-${item.cle}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.cle, item.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-border hover:bg-secondary transition-colors"
                            data-testid={`increase-quantity-${item.cle}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Remove & Price */}
                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => removeItem(item.cle)}
                          className="text-xs text-text_secondary hover:text-destructive transition-colors"
                          data-testid={`remove-item-${item.cle}`}
                        >
                          {t('remove')}
                        </button>
                        <span className="font-bold text-text_primary">
                          {formatPrice(prixLigne(item) * item.quantity, language)}
                        </span>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>
            
            {/* Footer */}
            {items.length > 0 && (
              <div className="p-4 border-t border-border bg-white">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-text_secondary">{t('subtotal')}</span>
                  <span className="text-xl font-bold text-text_primary" data-testid="cart-subtotal">
                    {formatPrice(subtotal, language)}
                  </span>
                </div>
                {/* Les tarifs et le minimum de commande sont annoncés ici, et
                    non après le formulaire de caisse : un client hors région
                    liégeoise découvrait le minimum de 75 € juste avant de
                    payer, après avoir tout rempli. */}
                <div className="mb-4 rounded-xl bg-secondary/30 p-3 text-xs text-text_secondary">
                  <p className="font-medium text-text_primary mb-1.5">
                    {language === 'fr' ? 'Frais de livraison' : 'Delivery fees'}
                  </p>
                  <ul className="space-y-0.5">
                    <li className="flex justify-between gap-2">
                      <span>Liège</span>
                      <span className="font-medium">{formatPrice(FRAIS_LIEGE, language)}</span>
                    </li>
                    <li className="flex justify-between gap-2">
                      <span>{language === 'fr' ? 'Périphérie de Liège' : 'Liège outskirts'}</span>
                      <span className="font-medium">{formatPrice(FRAIS_PERIPHERIE, language)}</span>
                    </li>
                    <li className="flex justify-between gap-2">
                      <span>{language === 'fr' ? 'Reste de la Belgique' : 'Rest of Belgium'}</span>
                      <span className="font-medium">{formatPrice(FRAIS_HORS_LIEGE, language)}</span>
                    </li>
                  </ul>

                  <p className="mt-2 pt-2 border-t border-border/60">
                    {language === 'fr'
                      ? `Livraison sous ${delaiLivraison('fr')} après confirmation.`
                      : `Delivered within ${delaiLivraison('en')} after confirmation.`}
                  </p>

                  {manqueHorsLiege > 0 ? (
                    <p className="mt-2 pt-2 border-t border-border/60 text-orange-700">
                      {language === 'fr'
                        ? `Livraison hors région liégeoise à partir de ${formatPrice(MINIMUM_HORS_LIEGE, language)} de commande : il vous manque ${formatPrice(manqueHorsLiege, language)}.`
                        : `Delivery outside the Liège area starts at ${formatPrice(MINIMUM_HORS_LIEGE, language)}: ${formatPrice(manqueHorsLiege, language)} to go.`}
                    </p>
                  ) : (
                    <p className="mt-2 pt-2 border-t border-border/60 text-green-700 font-medium">
                      ✓ {language === 'fr'
                        ? 'Minimum atteint pour toute la Belgique'
                        : 'Minimum reached for the whole of Belgium'}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary_hover transition-colors hover:scale-[1.02] active:scale-[0.98]"
                  data-testid="checkout-button"
                >
                  {t('checkout')}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
